import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Consistent status color scheme for BayesPrep Admin Portal
 * Provides unified color coding across all components
 */
export const statusColors = {
  // Primary statuses
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-800 border-slate-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',

  // Student-specific statuses
  graduated: 'bg-blue-100 text-blue-800 border-blue-200',
  transferred: 'bg-purple-100 text-purple-800 border-purple-200',
  suspended: 'bg-red-100 text-red-800 border-red-200',

  // Test statuses - distinct colors for each status
  draft: 'bg-slate-100 text-slate-800 border-slate-200',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  on_demand: 'bg-green-100 text-green-800 border-green-200',
  assigned: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  published: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  published_assigned: 'bg-violet-100 text-violet-800 border-violet-200',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
  finished: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  editing: 'bg-purple-100 text-purple-800 border-purple-200',
  archived: 'bg-gray-100 text-gray-800 border-gray-200',
  unknown: 'bg-slate-100 text-slate-800 border-slate-200',

  // Class statuses
  open: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed: 'bg-slate-100 text-slate-800 border-slate-200',

  // System statuses
  healthy: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  disabled: 'bg-slate-100 text-slate-800 border-slate-200',

  // Difficulty levels
  easy: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  hard: 'bg-red-100 text-red-800 border-red-200',
} as const;

/**
 * Get status color classes for a given status
 * @param status - The status string (case-insensitive)
 * @param fallback - Fallback color scheme if status not found
 * @returns CSS classes for the status
 */
export function getStatusColor(status: string, fallback: keyof typeof statusColors = 'inactive'): string {
  const normalizedStatus = status?.toLowerCase().replace(/[_\s]/g, '_') as keyof typeof statusColors;
  return statusColors[normalizedStatus] || statusColors[fallback];
}

/**
 * Status badge component utility
 * @param status - The status string
 * @param className - Additional CSS classes
 * @returns Complete CSS classes for a status badge
 */
export function getStatusBadgeClasses(status: string, className?: string): string {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 text-xs font-medium border';
  const statusClasses = getStatusColor(status);
  return cn(baseClasses, statusClasses, className);
}

export function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
